import React, { useEffect, useState, useMemo, useRef } from 'react';
import { GenericModal } from 'src/components/Modals/GenericModal';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { CloseIcon } from 'src/components/SvgIcons/SvgIcons';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Box, Typography } from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'src/components/Highcharts';
import { useQueryClient } from '@tanstack/react-query';
import {
  GenerationStatusEnum,
  type ContentGenerationRecord
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Generate/types';
import { CONTENT_GENERATION_QUERY_KEY } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Generate/components/ProductTable/useProductTable';
import colors from 'src/utils/colors';

interface GeneratingModalProps {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  projectId?: string;
}

/** Extracts records from the content generation query cache for the given projectId */
const getProjectRecordsFromCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string
): ContentGenerationRecord[] => {
  // Get ALL content generation queries and filter records by their projectId field.
  // This handles the case where a query was made before the project was created
  // (query key has no projectId), but websocket updates have since set projectId on the records.
  const queries = queryClient.getQueriesData<{
    pages?: { records: ContentGenerationRecord[] }[];
  }>({
    queryKey: [CONTENT_GENERATION_QUERY_KEY]
  });

  // Use a Map to deduplicate records by retailerSku
  const recordsByRetailerSku = new Map<string, ContentGenerationRecord>();

  for (const [, data] of queries) {
    if (data?.pages) {
      for (const page of data.pages) {
        if (page?.records) {
          for (const record of page.records) {
            // Filter by record's projectId field (set by websocket updates)
            if (record.projectId === projectId) {
              // Use the most recent record for each retailerSku
              const existing = recordsByRetailerSku.get(record.retailerSku);
              if (!existing || (record.updatedAt && existing.updatedAt && record.updatedAt > existing.updatedAt)) {
                recordsByRetailerSku.set(record.retailerSku, record);
              }
            }
          }
        }
      }
    }
  }

  return Array.from(recordsByRetailerSku.values());
};

const MAX_PROGRESS = 95; // Max progress before completion

const ModalContent = ({ onBack, onClose, projectId }: Omit<GeneratingModalProps, 'open'>) => {
  const queryClient = useQueryClient();
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const chartRef = useRef<{ chart: Highcharts.Chart } | null>(null);

  // Store project records in state - updated via cache subscription
  const [projectRecords, setProjectRecords] = useState<ContentGenerationRecord[]>(() =>
    projectId ? getProjectRecordsFromCache(queryClient, projectId) : []
  );

  // Subscribe to query cache updates for content generation queries
  useEffect(() => {
    if (!projectId) return;

    // Initial fetch from cache
    setProjectRecords(getProjectRecordsFromCache(queryClient, projectId));

    const queryCache = queryClient.getQueryCache();
    const unsubscribe = queryCache.subscribe((event) => {
      // Only react to updates for content generation queries
      const queryKey = event?.query?.queryKey;
      if (
        queryKey?.[0] === CONTENT_GENERATION_QUERY_KEY &&
        (event.type === 'updated' || event.type === 'added')
      ) {
        setProjectRecords(getProjectRecordsFromCache(queryClient, projectId));
      }
    });
    return unsubscribe;
  }, [queryClient, projectId]);

  const { readyCount, totalCount } = useMemo(() => {
    const completedCount = projectRecords.filter(
      (item) =>
        item.status === GenerationStatusEnum.enum.READY_TO_REVIEW ||
        item.status === GenerationStatusEnum.enum.GENERATION_ERROR ||
        item.status === GenerationStatusEnum.enum.REVIEWED
    ).length;
    return { readyCount: completedCount, totalCount: projectRecords.length };
  }, [projectRecords]);

  const hasAllCompleted = totalCount > 0 && readyCount === totalCount;

  // Progress: slowly increment from 0, jump to 100 when all complete
  const progress = hasAllCompleted ? 100 : animatedProgress;

  useEffect(() => {
    if (hasAllCompleted) {
      onClose();
      return;
    }

    // Slowly increment progress while not complete
    const interval = setInterval(() => {
      setAnimatedProgress((prev) => Math.min(prev + 1, MAX_PROGRESS));
    }, 1000);
    return () => clearInterval(interval);
  }, [hasAllCompleted, onClose]);

  // Initial chart options - stable reference, only created once
  const initialChartOptions = useMemo(
    (): Highcharts.Options => ({
      chart: {
        type: 'solidgauge',
        backgroundColor: 'transparent',
        borderRadius: 999,
        borderWidth: 0,
        width: 220,
        height: 220,
        margin: 0,
        spacing: [0, 0, 0, 0]
      },
      pane: {
        startAngle: 0,
        endAngle: 360,
        background: [
          {
            outerRadius: '0',
            innerRadius: '85%',
            backgroundColor: '#FFFFFF',
            borderWidth: 0
          }
        ]
      },
      yAxis: {
        min: 0,
        max: 1,
        lineWidth: 0,
        tickPositions: []
      },
      plotOptions: {
        solidgauge: {
          dataLabels: {
            enabled: false
          },
          linecap: 'round',
          stickyTracking: false,
          rounded: true
        }
      },
      tooltip: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      credits: { enabled: false },
      series: [
        {
          type: 'solidgauge',
          data: [
            {
              y: 1,
              color: colors.lightestGrey,
              radius: '96%',
              innerRadius: '82%'
            },
            {
              y: 0,
              color: colors.darkBlue,
              radius: '95%',
              innerRadius: '86%'
            }
          ]
        }
      ],
      title: {
        text: `<div style="text-align: center;">
                  <div style="font-size: 26px; font-weight: 700; line-height: 40px; color: ${colors.darkBlue}; font-family: Roboto, sans-serif; -webkit-font-smoothing: antialiased;">0%</div>
                  <div style="font-size: 16px; font-weight: 700; line-height: 24px; color: ${colors.darkBlue}; font-family: Roboto, sans-serif; -webkit-font-smoothing: antialiased;">Generated</div>
                </div>`,
        useHTML: true,
        verticalAlign: 'middle'
      }
    }),
    []
  );

  // Update chart data imperatively when progress changes (no re-render)
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    // Update the series data (second point is the progress)
    if (chart.series[0]) {
      chart.series[0].setData(
        [
          {
            y: 1,
            color: colors.lightestGrey,
            radius: '96%',
            innerRadius: '82%'
          },
          {
            y: progress / 100,
            color: colors.darkBlue,
            radius: '95%',
            innerRadius: '86%'
          }
        ],
        false // don't redraw yet
      );
    }

    // Update the title
    chart.setTitle({
      text: `<div style="text-align: center;">
                <div style="font-size: 26px; font-weight: 700; line-height: 40px; color: ${colors.darkBlue}; font-family: Roboto, sans-serif; -webkit-font-smoothing: antialiased;">${progress}%</div>
                <div style="font-size: 16px; font-weight: 700; line-height: 24px; color: ${colors.darkBlue}; font-family: Roboto, sans-serif; -webkit-font-smoothing: antialiased;">Generated</div>
              </div>`
    });

    // Redraw with animation
    chart.redraw(true);
  }, [progress]);

  return (
    <>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        className="genericModal__header"
        sx={{
          padding: '24px 24px 16px 24px',
          position: 'relative',
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '-50px',
            width: 'calc(100% + 100px)',
            height: '1px',
            backgroundColor: '#E8E8EC'
          }
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            lineHeight: '28px',
            color: '#000000',
            marginLeft: 'auto !important',
            marginRight: 'auto !important',
            marginBottom: '18px !important'
          }}
        >
          Generating Content
        </Typography>
        <CloseIcon
          style={{
            cursor: 'pointer',
            width: '16px',
            marginBottom: 'auto',
            height: '16px',
            marginTop: '6px'
          }}
          onClick={onClose}
        />
      </Flex>

      <GenericModal.Body>
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          sx={{
            textAlign: 'center',
            marginBottom: '54px'
          }}
        >
          {/* Progress Gauge */}
          <Box
            sx={{
              position: 'relative',
              borderRadius: '999px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1,
              marginTop: '12px',
              marginBottom: '4px'
            }}
          >
            <HighchartsReact highcharts={Highcharts} options={initialChartOptions} ref={chartRef} />
          </Box>
          {/* Heading */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '28px',
              marginBottom: '8px',
              color: '#000000'
            }}
          >
            Your content is generating
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              color: colors.actualSpend
            }}
          >
            We&apos;ll notify you when your content is generated.<br />You can close this window in the meantime.
          </Typography>
        </Flex>
      </GenericModal.Body>
      <GenericModal.Footer>
        <Flex justifyContent="center">
          <SlButton
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: colors.darkBlue,
              color: 'white',
              fontSize: '14px',
              fontWeight: 500,
              padding: '8px 24px',
              textTransform: 'none',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#0f1419'
              }
            }}
          >
            Close
          </SlButton>
        </Flex>
      </GenericModal.Footer>
    </>
  );
};

export const GeneratingModal = ({ open, onClose, onBack, projectId }: GeneratingModalProps) => {
  return (
    <GenericModal
      open={open}
      onClose={onClose}
      sx={{
        '-webkit-font-smoothing': 'antialiased',
        '-moz-osx-font-smoothing': 'grayscale',
        '& .MuiPaper-root': {
          width: '450px',
          height: '500px',
          maxHeight: '90vh',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          padding: '18px 24px 16px'
        },
        '& .genericModal__body': {
          display: 'flex',
          flexDirection: 'column',
          height: '372px',
          padding: '0'
        },
        '& .genericModal__header': {
          padding: '0'
        },
        '& .genericModal__footer': {
          marginTop: 'auto'
        }
      }}
    >
      <ModalContent onBack={onBack} onClose={onClose} projectId={projectId} />
    </GenericModal>
  );
};
